"use client";

import { Frame, Hash, Image as ImageIcon, Type } from "lucide-react";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import { useSequence } from "../use-sequence";
import { AppWindow, Cursor } from "../visual-kit";

// =============================================================================

// 0 wireframe · 1 navbar · 2 title · 3 button · 4 image · 5 final (no selection)
const DURATIONS = [1500, 1100, 1100, 1100, 1100, 2800];

const TARGETS = ["", "navbar", "title", "button", "image", ""] as const;
type Target = (typeof TARGETS)[number];

const CURSOR: Record<Exclude<Target, "">, { left: string; top: string }> = {
  navbar: { left: "36%", top: "24%" },
  title: { left: "40%", top: "47%" },
  button: { left: "33%", top: "70%" },
  image: { left: "66%", top: "52%" },
};

const LAYER_TARGETS: (Target | "frame")[] = [
  "frame",
  "navbar",
  "",
  "title",
  "button",
  "image",
];

const FILLS: Record<string, string> = {
  "": "#FFFFFF",
  navbar: "#14281D",
  title: "#14281D",
  button: "#C9A227",
  image: "#E07A3F",
};

// -----------------------------------------------------------------------------

function Selection({
  show,
  children,
  className,
}: {
  show: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("relative", className)}>
      {children}
      <motion.span
        aria-hidden
        className="pointer-events-none absolute -inset-[0.4cqw] rounded-[0.2cqw] border-[0.15cqw] border-[#0d99ff]"
        initial={false}
        animate={{ opacity: show ? 1 : 0 }}
        transition={{ duration: 0.2 }}
      >
        {[
          "-top-[0.45cqw] -left-[0.45cqw]",
          "-top-[0.45cqw] -right-[0.45cqw]",
          "-bottom-[0.45cqw] -left-[0.45cqw]",
          "-right-[0.45cqw] -bottom-[0.45cqw]",
        ].map((position) => (
          <span
            key={position}
            className={cn(
              "absolute size-[0.75cqw] border-[0.15cqw] border-[#0d99ff] bg-white",
              position
            )}
          />
        ))}
      </motion.span>
    </div>
  );
}

/**
 * Step 4: a design tool where the wireframe turns into the final mockup.
 */
export function MockupVisual({ active }: { active: boolean }) {
  const t = useTranslations("Home.process.visuals.mockup");
  const step = useSequence(active, DURATIONS);
  const nav = t.raw("nav") as string[];
  const layerNames = t.raw("layerNames") as string[];

  const target = TARGETS[step];
  const styled = {
    navbar: step >= 1,
    title: step >= 2,
    button: step >= 3,
    image: step >= 4,
  };

  return (
    <AppWindow>
      <div className="flex size-full flex-col">
        {/* Top bar */}
        <div className="flex h-[3.4cqw] shrink-0 items-center justify-between bg-[#2c2c2c] px-[1.2cqw] text-white">
          <span className="flex gap-[0.5cqw]">
            {["#ff5f57", "#febc2e", "#28c840"].map((color) => (
              <span
                key={color}
                className="size-[0.85cqw] rounded-full"
                style={{ background: color }}
              />
            ))}
          </span>
          <span className="text-[1cqw] text-white/80">{t("file")}</span>
          <span className="rounded-[0.4cqw] bg-[#0d99ff] px-[0.9cqw] py-[0.25cqw] text-[0.85cqw]">
            ▶
          </span>
        </div>

        <div className="flex min-h-0 flex-1">
          {/* Layers */}
          <aside className="w-[17%] shrink-0 border-r border-black/[0.08] py-[1cqw] text-[0.95cqw]">
            <p className="px-[1.1cqw] pb-[0.8cqw] font-semibold">
              {t("layers")}
            </p>
            {layerNames.map((name, index) => {
              const isSelected =
                target !== "" && LAYER_TARGETS[index] === target;
              const Icon =
                index === 0
                  ? Frame
                  : index === 5
                    ? ImageIcon
                    : index === 3
                      ? Type
                      : Hash;
              return (
                <p
                  key={name}
                  className={cn(
                    "flex items-center gap-[0.6cqw] py-[0.45cqw] transition-colors duration-200",
                    index === 0 ? "px-[1.1cqw]" : "pr-[1.1cqw] pl-[2.2cqw]",
                    isSelected && "bg-[#e5f4ff] text-[#0d99ff]"
                  )}
                >
                  <Icon className="size-[1cqw] opacity-60" />
                  {name}
                </p>
              );
            })}
          </aside>

          {/* Canvas */}
          <div className="relative flex min-w-0 flex-1 items-center justify-center bg-[#e9e9e9]">
            <div className="relative flex aspect-[16/10] w-[86%] flex-col overflow-hidden rounded-[0.5cqw] bg-white shadow-[0_0.5cqw_1.5cqw_rgb(0_0_0/0.1)]">
              {/* Navbar */}
              <Selection show={target === "navbar"} className="mx-[3%] mt-[3%]">
                <motion.div
                  className="flex items-center justify-between rounded-[0.4cqw] px-[1.2cqw] py-[0.9cqw]"
                  initial={false}
                  animate={{
                    backgroundColor: styled.navbar ? "#14281D" : "#ededed",
                  }}
                  transition={{ duration: 0.5 }}
                >
                  {styled.navbar ? (
                    <>
                      <span className="text-[1.15cqw] font-bold tracking-tight text-[#E9D8A6]">
                        {t("brand")}
                      </span>
                      <span className="flex gap-[1.2cqw] text-[0.85cqw] text-white/80">
                        {nav.map((item) => (
                          <span key={item}>{item}</span>
                        ))}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="h-[1cqw] w-[7cqw] rounded-full bg-[#d4d4d4]" />
                      <span className="flex gap-[1cqw]">
                        {[0, 1, 2].map((item) => (
                          <span
                            key={item}
                            className="h-[0.8cqw] w-[3.2cqw] rounded-full bg-[#d4d4d4]"
                          />
                        ))}
                      </span>
                    </>
                  )}
                </motion.div>
              </Selection>

              <div className="grid flex-1 grid-cols-[1.1fr_1fr] items-center gap-[4%] px-[6%]">
                <div className="flex flex-col gap-[1.4cqw]">
                  {/* Title */}
                  <Selection show={target === "title"}>
                    {styled.title ? (
                      <motion.p
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-[2.2cqw] leading-[1.05] font-extrabold tracking-[-0.03em] text-[#14281D]"
                      >
                        {t("headline")}
                      </motion.p>
                    ) : (
                      <div className="flex flex-col gap-[0.7cqw]">
                        <span className="h-[1.6cqw] w-full rounded-full bg-[#d4d4d4]" />
                        <span className="h-[1.6cqw] w-4/5 rounded-full bg-[#d4d4d4]" />
                        <span className="h-[1.6cqw] w-3/5 rounded-full bg-[#d4d4d4]" />
                      </div>
                    )}
                  </Selection>
                  <div className="flex flex-col gap-[0.5cqw]">
                    <span className="h-[0.7cqw] w-full rounded-full bg-[#ececec]" />
                    <span className="h-[0.7cqw] w-11/12 rounded-full bg-[#ececec]" />
                  </div>
                  {/* Button */}
                  <Selection show={target === "button"} className="w-fit">
                    <motion.span
                      className="block rounded-full px-[1.6cqw] py-[0.8cqw] text-[1cqw] font-semibold"
                      initial={false}
                      animate={{
                        backgroundColor: styled.button ? "#C9A227" : "#ffffff",
                        color: styled.button ? "#14281D" : "#bdbdbd",
                        boxShadow: styled.button
                          ? "0 0 0 0px #d4d4d4"
                          : "0 0 0 1.5px #d4d4d4",
                      }}
                      transition={{ duration: 0.45 }}
                    >
                      {t("cta")}
                    </motion.span>
                  </Selection>
                </div>

                {/* Image */}
                <Selection show={target === "image"} className="h-[78%]">
                  <motion.div
                    className="relative size-full overflow-hidden rounded-[1cqw]"
                    initial={false}
                    animate={{
                      backgroundColor: styled.image ? "#E07A3F" : "#efefef",
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    {styled.image ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 1.1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7 }}
                        className="absolute inset-0 grid place-items-center bg-[radial-gradient(circle_at_30%_25%,#ffd27a,#e07a3f_45%,#7a2e16)] text-[6cqw]"
                      >
                        🍝
                      </motion.div>
                    ) : (
                      <svg
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                        className="absolute inset-0 size-full"
                        aria-hidden
                      >
                        <path
                          d="M0 0 L100 100 M100 0 L0 100"
                          stroke="#d4d4d4"
                          strokeWidth="0.8"
                        />
                      </svg>
                    )}
                  </motion.div>
                </Selection>
              </div>
            </div>

            {/* Cursor */}
            <motion.div
              className="absolute"
              initial={false}
              animate={
                target
                  ? { ...CURSOR[target], opacity: 1 }
                  : { left: "80%", top: "84%", opacity: step === 0 ? 0 : 1 }
              }
              transition={{ duration: 0.75, ease: [0.45, 0, 0.2, 1] }}
            >
              <Cursor color="#0d99ff" />
            </motion.div>
          </div>

          {/* Properties */}
          <aside className="w-[19%] shrink-0 border-l border-black/[0.08] px-[1.1cqw] py-[1cqw] text-[0.9cqw]">
            <p className="pb-[1cqw] font-semibold">{t("design")}</p>
            <p className="text-black/50">{t("fill")}</p>
            <div className="mt-[0.5cqw] flex items-center gap-[0.6cqw] rounded-[0.4cqw] bg-black/[0.04] px-[0.6cqw] py-[0.5cqw]">
              <motion.span
                className="size-[1.2cqw] rounded-[0.25cqw] ring-1 ring-black/10"
                initial={false}
                animate={{ backgroundColor: FILLS[target] }}
              />
              <span className="font-mono">{FILLS[target]}</span>
            </div>
            <p className="mt-[1.2cqw] text-black/50">{t("font")}</p>
            <div className="mt-[0.5cqw] rounded-[0.4cqw] bg-black/[0.04] px-[0.6cqw] py-[0.5cqw]">
              Sora · {target === "title" ? "800" : "500"}
            </div>
            <div className="mt-[0.6cqw] grid grid-cols-2 gap-[0.5cqw]">
              <span className="rounded-[0.4cqw] bg-black/[0.04] px-[0.6cqw] py-[0.5cqw]">
                {target === "title" ? "48" : "16"}
              </span>
              <span className="rounded-[0.4cqw] bg-black/[0.04] px-[0.6cqw] py-[0.5cqw]">
                Auto
              </span>
            </div>
          </aside>
        </div>
      </div>
    </AppWindow>
  );
}

// =============================================================================
